import type React from 'react'
import {useEffect, useRef} from 'react'

import {waitForConsentPreferences} from '@github-ui/cookie-consent'
import {doNotTrack} from '@github-ui/do-not-track'

import type {VendorPixel} from '../../lib/types/analytics/vendor-pixel'
import {firePixelsOnClick} from '../../lib/vendor-pixels/fire-vendor-pixel'
import {usePixelsOnPageview} from '../../lib/vendor-pixels/use-pixels-on-pageview'

export const VENDOR_PIXEL_EVENT_ATTR = 'data-vendor-pixel-event'

interface VendorPixelTrackerProps {
  vendorPixels: VendorPixel[]
  children: React.ReactNode
}

/**
 * Event delegation component that fires vendor tracking pixels when a user
 * clicks an element annotated with `data-analytics-event` (new flow) or
 * `data-vendor-pixel-event` (legacy flow).
 *
 * Place this as an ancestor of any elements that should trigger pixel tracking.
 * The component is inert when vendorPixels is empty (path not enabled).
 *
 * For new-shape pixels (those carrying `type: 'cta-click'`), each (pixel, CTA)
 * combination fires at most once per mount — the dedup key is composed of the
 * pixel's vendor key and the click payload. For legacy pixels the dedup key
 * collapses to the vendor key alone, preserving the original "fire once per
 * page visit" behavior.
 *
 * Respects user privacy preferences: pixels are not fired when Do Not Track /
 * Global Privacy Control is enabled, or when the user has not consented to
 * Analytics cookies.
 *
 * Also fires "page view" pixels once on mount for vendors that provide a
 * `pageViewUrl` / new-shape `type: 'page-view'` pixel, using the same privacy
 * checks.
 */
export function VendorPixelTracker({vendorPixels, children}: VendorPixelTrackerProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Fire "page view" pixels once on mount (consent- and DNT-gated internally)
  usePixelsOnPageview(vendorPixels)

  // This keeps track of the pixel vendor keys that have fired to make sure it fires only once
  const firedKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const el = ref.current
    if (!el || vendorPixels.length === 0) return

    if (doNotTrack()) return

    // Guard against attaching listeners after unmount (waitForConsentPreferences is async)
    let unmounted = false
    const firedKeys = firedKeysRef.current

    const handler = (event: MouseEvent) => {
      const clickEl = event.target as HTMLElement | null
      const analyticsEl = clickEl?.closest?.<HTMLElement>('[data-analytics-event]')
      const legacyEl = clickEl?.closest?.<HTMLElement>(`[${VENDOR_PIXEL_EVENT_ATTR}]`)
      if (!analyticsEl && !legacyEl) return

      let payload: Record<string, unknown> | null = null
      if (analyticsEl) {
        const rawPayload = analyticsEl.getAttribute('data-analytics-event') ?? '{}'
        try {
          payload = JSON.parse(rawPayload)
        } catch {
          // Malformed JSON — null payload. Legacy unscoped pixels still fire
          // if a legacy element matched; new-shape scoped pixels won't.
          payload = null
        }
      }
      firePixelsOnClick(vendorPixels, payload, firedKeys)
    }

    async function attachListener() {
      const consentPreferences = await waitForConsentPreferences()
      if (unmounted || !el) return
      if (consentPreferences && consentPreferences['Analytics']) {
        el.addEventListener('click', handler)
      }
    }

    attachListener()

    return () => {
      unmounted = true
      el.removeEventListener('click', handler)
      // Reset so pixels can fire again on next mount / page visit
      firedKeys.clear()
    }
  }, [vendorPixels])

  return <div ref={ref}>{children}</div>
}
